import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Divider, LinearProgress, Stack } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import API from '../../../../services/API'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import { VehicleContext } from '../SingleVehicle'
import { SectionHeader } from './SectionKit'
import LolerSection from './Loler/LolerSection'

// LOLER is always shown (it's a legal requirement whenever the vehicle has
// lifting equipment); the scheduled safety checks only when the tenant has
// them switched on (Site Configuration → Vehicles).
const SafetyChecksSection = ({ safetyChecksEnabled }) => (
  <Stack spacing={3}>
    <LolerSection />
    {safetyChecksEnabled && (
      <>
        <Divider />
        <ScheduledSafetyChecks />
      </>
    )}
  </Stack>
)

const ScheduledSafetyChecks = () => {
  const { vehicle } = useContext(VehicleContext)
  const [loading, setLoading] = useState(true)
  const [safetyChecks, setSafetyChecks] = useState([])
  const navigate = useNavigate()

  useEffect(() => {
    API.get('/fleet/vehicle-safety-checks', {
      params: { vehicle: vehicle._id },
    })
      .then((res) => res.status === 200 && setSafetyChecks(res.data))
      .finally(() => setLoading(false))
  }, [vehicle._id])

  return (
    <Stack spacing={1.5}>
      <SectionHeader
        title="Vehicle Safety Checks"
        caption="Scheduled roadworthiness inspections recorded for this vehicle"
        action={
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() =>
              navigate('/fleet/record-safety-check?vehicle=' + vehicle._id)
            }
          >
            Record Safety Check
          </Button>
        }
      />
      {loading ? (
        <LinearProgress />
      ) : safetyChecks.length === 0 ? (
        <Alert severity="info">No safety checks recorded yet.</Alert>
      ) : (
        <CustomDataGrid
          getRowId={(row) => row._id}
          rows={safetyChecks}
          columns={[
            {
              headerName: 'Date',
              field: 'testDate',
              renderCell: ({ value }) => moment(value).format('DD/MM/YYYY'),
              minWidth: 100,
              flex: 1,
            },
            {
              headerName: 'Mileage',
              field: 'mileage',
              minWidth: 100,
            },
            {
              headerName: 'Safe',
              field: 'safeCount',
              minWidth: 60,
              renderCell: ({ row }) => row?.testStats?.safeCount ?? '',
              align: 'center',
              headerAlign: 'center',
            },
            {
              headerName: 'N/A',
              field: 'naCount',
              minWidth: 60,
              renderCell: ({ row }) => row?.testStats?.naCount ?? '',
              align: 'center',
              headerAlign: 'center',
            },
            {
              headerName: 'Part',
              field: 'partCount',
              minWidth: 60,
              renderCell: ({ row }) => row?.testStats?.partCount ?? '',
              align: 'center',
              headerAlign: 'center',
            },
            {
              headerName: 'Advisories',
              field: 'advisoryCount',
              minWidth: 60,
              renderCell: ({ row }) => row?.testStats?.advisoryCount ?? '',
              align: 'center',
              headerAlign: 'center',
            },
            {
              headerName: 'Total Answered',
              field: 'totalAnswered',
              minWidth: 60,
              renderCell: ({ row }) => row?.testStats?.totalAnswered ?? '',
              align: 'center',
              headerAlign: 'center',
            },
          ]}
          slots={{ toolbar: () => null, footer: () => null }}
          disableRowSelectionOnClick
          onRowClick={({ row }) =>
            navigate('/fleet/vehicle-safety-check/' + row._id)
          }
        />
      )}
    </Stack>
  )
}

export default SafetyChecksSection
